import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useSWRConfig } from 'swr';
import { subscribeChat } from '../../../clients/chat/connection';
import { InternalEventType } from '../../../clients/chat/types';
import endPoints from '../../../services/endpoints';
import { getSelectedTabKeyFromUrl } from '../../../store/childStore/tabStore';
import { useCurrentChatDispatch } from '../../../store/provider/CurrentChatContextProvider';
import { getTenantId } from '../../../utils/accountHelper';
import { getChatPlugin } from '../../../utils/store/pluginStore';
import { filterSWRKeys } from '../../../utils/swr';

export default function useChatSessionConnectionState(chatSessionId: string, tabKey: string, isClosedTicket = false) {
    const {
        data: { accessToken }
    } = useSession();
    const { asPath } = useRouter();
    const [subscribedToChatSession, setSubscribedToChatSession] = useState(false);
    const dispatch = useCurrentChatDispatch();

    const { mutate } = useSWRConfig();
    const selectedTabKey = getSelectedTabKeyFromUrl(asPath);
    const isCurrentTab = selectedTabKey === tabKey;

    useEffect(() => {
        isCurrentTab && dispatch({ type: 'SET_CHAT_SESSION_ID', payload: chatSessionId });
    }, [dispatch, isCurrentTab, chatSessionId]);
    useEffect(() => {
        const subscribedToChatSessionHandler = (_event: any) => {
            setSubscribedToChatSession(true);
            // re-fetch chatEntities and update chatId/chatSessionId info on corresponding tab
            mutate(filterSWRKeys(endPoints.chat.activeChatEntities));
        };

        const disconnectHandler = () => {
            setSubscribedToChatSession(false);
            // re-fetch chatEntities and reset chatId/chatSessionId info on corresponding tab
            mutate(filterSWRKeys(endPoints.chat.activeChatEntities));
        };
        window.addEventListener(
            `${InternalEventType.SUBSCRIBED_TO_CHAT}-${chatSessionId}`,
            subscribedToChatSessionHandler
        );
        window.addEventListener(`${InternalEventType.CHAT_DISCONNECTED}-${chatSessionId}`, disconnectHandler);
        return () => {
            window.removeEventListener(`${InternalEventType.CHAT_DISCONNECTED}-${chatSessionId}`, disconnectHandler);
            window.removeEventListener(
                `${InternalEventType.SUBSCRIBED_TO_CHAT}-${chatSessionId}`,
                subscribedToChatSessionHandler
            );
        };
    }, [tabKey, chatSessionId, mutate]);

    useEffect(() => {
        !isClosedTicket &&
            !selectedTabKey?.includes('new') &&
            chatSessionId &&
            subscribeChat({
                brokerUrl: getChatPlugin()?.manifest?.src,
                chatSessionId,
                tenantId: getTenantId(),
                token: accessToken
            });
    }, [accessToken, chatSessionId, isClosedTicket, selectedTabKey]);

    useEffect(() => {
        isCurrentTab && dispatch({ type: 'SET_CHAT_SESSION_STATE', payload: subscribedToChatSession });
    }, [dispatch, isCurrentTab, subscribedToChatSession]);

    return { subscribedToChatSession };
}
